import React, { useEffect, useState } from "react";
import Wrapper from "../wrapper";
import "./styles.scss";
import SearchBar from "./searchBar";
import moment from "moment/moment";
import { useGlobalContext } from "../../../store/global";
import { getRecipes, getRecipeCategories } from "../../../utils/data";
import { useTranslation } from "react-i18next";

import 'moment/locale/en-gb';
import 'moment/locale/zh-cn';
import 'moment/locale/ja';
import 'moment/locale/ko';
import 'moment/locale/th';

const PAGE_SIZE = 10;

export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay ?? 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const parseTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.round(time - minutes * 60, 2);

  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const useMomentLocale = () => {
  useEffect(() => {
    const lang = localStorage.getItem("lang");
    switch (lang) {
      case "english":
        moment.locale("en");
        break;
      case "mandarin":
        moment.locale("zh-cn");
        break;
      case "japanese":
        moment.locale("ja");
        break;
      case "korean":
        moment.locale("ko");
        break;
      case "thai":
        moment.locale("th");
        break;
      default:
        moment.locale("en");
    }
  }, []);
};

const Directory = () => {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 500);

  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState();

  const [isAllEmpty, setIsAllEmpty] = useState({
    unitCount: 0,
    emptyCount: 0,
    fullCount: 0,
  });

  useEffect(() => {
    setIsLoading(true);
    getRecipeCategories().then((res) => {
      setIsLoading(false);
      setCategories(res?.data);
      const unitCount = (res?.data || []).reduce(
        (acc, cat) => acc + 1 + (cat.children?.length || 0),
        0
      );
      setIsAllEmpty((e) => ({ ...e, unitCount }));
    });
  }, []);

  useEffect(() => {
    setIsAllEmpty((e) => ({
      ...e,
      emptyCount: 0,
      fullCount: 0,
    }));
  }, [debouncedQ]);

  return (
    <Wrapper>
      <div id="content">
        <section className="classics">
          <div className="viewport">
            <div className="inner">
              <SearchBar
                searchValue={q}
                onSearchChange={setQ}
                onClick={() => {}}
              />

              <div className="list">
                {categories?.length > 0 && categories
                  ?.sort((a, b) => a.order - b.order)
                  .map((cat) => (
                    <RecipeGroup
                      key={cat.id}
                      category={cat}
                      q={debouncedQ}
                      onLoadedFull={() =>
                        setIsAllEmpty((e) => ({
                          ...e,
                          fullCount: e.fullCount + 1,
                        }))
                      }
                      onLoadedEmpty={() =>
                        setIsAllEmpty((e) => ({
                          ...e,
                          emptyCount: e.emptyCount + 1,
                        }))
                      }
                    />
                  ))}
                <div className="loader-wrapper">
                  {(isLoading ||
                    isAllEmpty.unitCount >
                      isAllEmpty.emptyCount + isAllEmpty.fullCount) &&
                    `${t("Loading")}...`}
                  {!isLoading &&
                    isAllEmpty.unitCount > 0 &&
                    isAllEmpty.unitCount === isAllEmpty.emptyCount &&
                    t("No_result_for_your_search")}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Wrapper>
  );
};

export default Directory;

const RecipeGroup = ({ category, q, onLoadedFull, onLoadedEmpty }) => {
  const { t } = useTranslation();
  const children = category.children;

  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setTotalCount(0);
  }, [q]);

  const handleChildCount = (count) => {
    setTotalCount((prev) => prev + count);
  };

  return (
    <div className="category-group">
      {totalCount > 0 && (
        <div className="category">
          <span className="name">{category.name}</span>
          <span className="count">
            {totalCount === 1
              ? t("XRecipe", { num: totalCount })
              : t("XRecipes", { num: totalCount })}
          </span>
        </div>
      )}
      <RecipeList
        category={category.id}
        name={category.name}
        q={q}
        showHeader={false}
        onLoadedFull={onLoadedFull}
        onLoadedEmpty={onLoadedEmpty}
        onCount={handleChildCount}
      />
      <div className="subcategories">
        {(children || [])
          .sort((a, b) => a.order - b.order)
          .map((child) => (
            <RecipeList
              key={child.id}
              category={child.id}
              name={child.name}
              q={q}
              onLoadedFull={onLoadedFull}
              onLoadedEmpty={onLoadedEmpty}
              onCount={handleChildCount}
            />
          ))}
      </div>
    </div>
  );
};

const RecipeList = ({
  category,
  name,
  q,
  onLoadedFull,
  onLoadedEmpty,
  onCount,
  showHeader = true,
}) => {
  const { t } = useTranslation();
  useMomentLocale();
  const [expanded, setExpanded] = useState(false);
  const [recipes, setRecipes] = useState();
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { openRecipeModal } = useGlobalContext();

  const getNextPage = async () => {
    setIsLoading(true);
    const nexPage = page + 1;
    const res = await getRecipes({
      q,
      category,
      range: `[${nexPage * PAGE_SIZE},${
        nexPage * PAGE_SIZE + (PAGE_SIZE - 1)
      }]`,
      sort: `["title", "ASC"]`,
    });
    setRecipes((e) => [...(e || []), ...(res?.data || [])]);
    setPage((e) => (res?.data.length < PAGE_SIZE ? null : nexPage));
    setIsLoading(false);
  };

  useEffect(() => {
    const launchSearch = async () => {
      const res = await getRecipes({
        q,
        category,
        range: `[0,${PAGE_SIZE - 1}]`,
        sort: `["title", "ASC"]`,
      });
      setRecipes(res?.data || []);
      const total = parseInt(
        res?.headers?.["content-range"]?.split(" ")[1]?.split("/")[1]
      );
      if (res?.data.length < PAGE_SIZE) {
        setPage(null);
      }

      if (res?.data.length > 0) {
        onLoadedFull();
      } else {
        onLoadedEmpty();
      }
      if (onCount) onCount(total ?? 0);

      setIsLoading(false);
    };

    setIsLoading(true);
    setRecipes(undefined);
    launchSearch();
    setPage(0);
  }, [q]);

  if (recipes === undefined) return null;

  const isEmpty = !isLoading && !recipes?.length;

  if (isEmpty) return null;

  return (
    <div className={showHeader ? `subcategory${expanded ? " expanded" : ""}` : "subcategory parent-recipes expanded"}>
      {showHeader && (
        <div
          className="subcategory-header"
          onClick={() => setExpanded((val) => !val)}
        >
          <span className="arrow" />
          <span className="name">{name}</span>
        </div>
      )}
      <div className="subcategory-content">
        <div className="recipe-list">
          {recipes?.map((recipe) => (
            <div
              key={recipe.id}
              className={`recipe${recipe.userComplete ? " watched" : ""}`}
              onClick={() =>
                openRecipeModal({
                  apId: recipe.id,
                  wistiaHashId: recipe.wistiaHashId,
                  description: recipe.ingredients,
                })
              }
            >
              <div className="wrapper-info">
                <div className="name">
                  <span className="icon-check" />
                  <span>{recipe.title}</span>
                </div>
                <div className="time">
                  <span>{parseTime(recipe.duration)}</span>
                  <span>
                    {t("Released_On", {
                      date: moment(recipe.createdAt).format("MMM DD, YYYY"),
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {page !== null && (
            <div className="load-wrapper">
              <button
                disabled={isLoading}
                className="tertiary vsmall"
                onClick={getNextPage}
              >
                <span>{isLoading ? t("Loading") : t("Load_More")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
